// components/RiskSettings.jsx
import React, { useState } from 'react';
import '../../styles/RiskSettings.css';

export const RiskSettings = ({ onUpdate, portfolioId }) => {
  const [settings, setSettings] = useState({
    maxPositionSize: 20,
    maxPortfolioRisk: 25,
    maxDrawdown: 15,
    varLimit: 50000,
    stopLossDefault: 10,
    trailingStopDefault: 15,
    riskPerTrade: 2,
    correlationLimit: 0.7,
    sectorConcentration: 30,
    marginLimit: 50,
    notifications: {
      varBreach: true,
      concentrationAlert: true,
      drawdownAlert: true,
      dailyReport: false,
      weeklySummary: true
    }
  });

  const [activeTab, setActiveTab] = useState('limits');
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('notifications.')) {
      const notificationKey = name.split('.')[1];
      setSettings(prev => ({
        ...prev,
        notifications: {
          ...prev.notifications,
          [notificationKey]: checked
        }
      }));
    } else {
      setSettings(prev => ({
        ...prev,
        [name]: type === 'number' ? parseFloat(value) : value
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const response = await onUpdate(settings);
    setSaving(false);
    setLastSaved(new Date().toLocaleTimeString());
    
    // Show success message
    alert('Risk settings updated successfully!');
  };

  const handleReset = () => {
    setSettings({
      maxPositionSize: 20,
      maxPortfolioRisk: 25,
      maxDrawdown: 15,
      varLimit: 50000,
      stopLossDefault: 10,
      trailingStopDefault: 15,
      riskPerTrade: 2,
      correlationLimit: 0.7,
      sectorConcentration: 30,
      marginLimit: 50,
      notifications: {
        varBreach: true,
        concentrationAlert: true,
        drawdownAlert: true,
        dailyReport: false,
        weeklySummary: true
      }
    });
  };

  const tabs = [
    { id: 'limits', label: 'Position Limits', icon: '📊' },
    { id: 'risk', label: 'Risk Parameters', icon: '⚠️' },
    { id: 'stops', label: 'Stop Settings', icon: '🎯' },
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'advanced', label: 'Advanced', icon: '⚙️' }
  ];

  return (
    <div className="panel risk-settings">
      <div className="panel-header">
        <h2>Risk Management Settings</h2>
        <span className="api-endpoint">PUT /rms/risk/settings/{portfolioId}</span>
      </div>

      <div className="panel-content">
        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="settings-content">
          {activeTab === 'limits' && (
            <div className="limits-settings">
              <h3>Position & Portfolio Limits</h3>
              <p className="section-description">
                Control maximum position sizes and portfolio concentration
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="maxPositionSize">Maximum Position Size (%)</label>
                  <p className="setting-description">
                    Maximum allocation to any single position
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="maxPositionSize"
                    name="maxPositionSize"
                    min="1"
                    max="50"
                    step="1"
                    value={settings.maxPositionSize}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.maxPositionSize}%</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="sectorConcentration">Maximum Sector Concentration (%)</label>
                  <p className="setting-description">
                    Maximum allocation to any single sector
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="sectorConcentration"
                    name="sectorConcentration"
                    min="10"
                    max="60"
                    step="5"
                    value={settings.sectorConcentration}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.sectorConcentration}%</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="marginLimit">Margin Limit (%)</label>
                  <p className="setting-description">
                    Maximum margin usage as percentage of portfolio
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="marginLimit"
                    name="marginLimit"
                    min="0"
                    max="100"
                    step="5"
                    value={settings.marginLimit}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.marginLimit}%</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="correlationLimit">Maximum Correlation Limit</label>
                  <p className="setting-description">
                    Maximum allowed correlation between new position and existing holdings
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="correlationLimit"
                    name="correlationLimit"
                    min="0.1"
                    max="0.9"
                    step="0.1"
                    value={settings.correlationLimit}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.correlationLimit.toFixed(1)}</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'risk' && (
            <div className="risk-settings">
              <h3>Risk Parameters</h3>
              <p className="section-description">
                Define risk thresholds and limits for portfolio management
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="maxPortfolioRisk">Maximum Portfolio Risk (%)</label>
                  <p className="setting-description">
                    Maximum allowed portfolio drawdown from peak
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="maxPortfolioRisk"
                    name="maxPortfolioRisk"
                    min="5"
                    max="40"
                    step="1"
                    value={settings.maxPortfolioRisk}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.maxPortfolioRisk}%</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="maxDrawdown">Maximum Daily Drawdown (%)</label>
                  <p className="setting-description">
                    Maximum allowed daily loss before reducing exposure
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="maxDrawdown"
                    name="maxDrawdown"
                    min="2"
                    max="20"
                    step="1"
                    value={settings.maxDrawdown}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.maxDrawdown}%</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="varLimit">VaR Limit ($)</label>
                  <p className="setting-description">
                    Maximum acceptable 1-day Value at Risk at 95% confidence
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="number"
                    id="varLimit"
                    name="varLimit"
                    min="1000"
                    max="100000"
                    step="1000"
                    value={settings.varLimit}
                    onChange={handleChange}
                  />
                  <span className="setting-value">${settings.varLimit.toLocaleString()}</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="riskPerTrade">Risk per Trade (%)</label>
                  <p className="setting-description">
                    Maximum risk per individual trade as percentage of portfolio
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="riskPerTrade"
                    name="riskPerTrade"
                    min="0.5"
                    max="5"
                    step="0.5"
                    value={settings.riskPerTrade}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.riskPerTrade}%</span>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'stops' && (
            <div className="stop-settings">
              <h3>Stop Loss Settings</h3>
              <p className="section-description">
                Configure default stop loss and trailing stop parameters
              </p>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="stopLossDefault">Default Stop Loss (%)</label>
                  <p className="setting-description">
                    Default stop loss percentage for new positions
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="stopLossDefault"
                    name="stopLossDefault"
                    min="5"
                    max="25"
                    step="1"
                    value={settings.stopLossDefault}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.stopLossDefault}%</span>
                </div>
              </div>

              <div className="setting-item">
                <div className="setting-info">
                  <label htmlFor="trailingStopDefault">Default Trailing Stop (%)</label>
                  <p className="setting-description">
                    Default trailing stop percentage for profitable positions
                  </p>
                </div>
                <div className="setting-control">
                  <input
                    type="range"
                    id="trailingStopDefault"
                    name="trailingStopDefault"
                    min="5"
                    max="30"
                    step="1"
                    value={settings.trailingStopDefault}
                    onChange={handleChange}
                  />
                  <span className="setting-value">{settings.trailingStopDefault}%</span>
                </div>
              </div>

              <div className="stop-presets">
                <h4>Stop Loss Presets</h4>
                <div className="preset-grid">
                  <button 
                    className="preset-btn"
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      stopLossDefault: 8,
                      trailingStopDefault: 12
                    }))}
                  >
                    <span className="preset-name">Conservative</span>
                    <span className="preset-stops">Stop: 8%, Trail: 12%</span>
                  </button>
                  
                  <button 
                    className="preset-btn"
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      stopLossDefault: 10,
                      trailingStopDefault: 15
                    }))}
                  >
                    <span className="preset-name">Balanced</span>
                    <span className="preset-stops">Stop: 10%, Trail: 15%</span>
                  </button>
                  
                  <button 
                    className="preset-btn"
                    onClick={() => setSettings(prev => ({
                      ...prev,
                      stopLossDefault: 15,
                      trailingStopDefault: 20
                    }))}
                  >
                    <span className="preset-name">Aggressive</span>
                    <span className="preset-stops">Stop: 15%, Trail: 20%</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="notification-settings">
              <h3>Alert Notifications</h3>
              <p className="section-description">
                Configure when to receive risk alerts and notifications
              </p>

              <div className="notification-list">
                <div className="notification-item">
                  <div className="notification-info">
                    <label htmlFor="varBreach">VaR Limit Breach</label>
                    <p className="notification-description">
                      Alert when portfolio VaR exceeds configured limit
                    </p>
                  </div>
                  <div className="notification-control">
                    <label className="switch">
                      <input
                        type="checkbox"
                        id="varBreach"
                        name="notifications.varBreach"
                        checked={settings.notifications.varBreach}
                        onChange={handleChange}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <label htmlFor="concentrationAlert">Concentration Alert</label>
                    <p className="notification-description">
                      Alert when position or sector concentration exceeds limits
                    </p>
                  </div>
                  <div className="notification-control">
                    <label className="switch">
                      <input
                        type="checkbox"
                        id="concentrationAlert"
                        name="notifications.concentrationAlert"
                        checked={settings.notifications.concentrationAlert}
                        onChange={handleChange}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <label htmlFor="drawdownAlert">Drawdown Alert</label>
                    <p className="notification-description">
                      Alert when portfolio drawdown reaches warning levels
                    </p>
                  </div>
                  <div className="notification-control">
                    <label className="switch">
                      <input
                        type="checkbox"
                        id="drawdownAlert"
                        name="notifications.drawdownAlert"
                        checked={settings.notifications.drawdownAlert}
                        onChange={handleChange}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <label htmlFor="dailyReport">Daily Risk Report</label>
                    <p className="notification-description">
                      Receive daily summary of portfolio risk metrics
                    </p>
                  </div>
                  <div className="notification-control">
                    <label className="switch">
                      <input
                        type="checkbox"
                        id="dailyReport"
                        name="notifications.dailyReport"
                        checked={settings.notifications.dailyReport}
                        onChange={handleChange}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>

                <div className="notification-item">
                  <div className="notification-info">
                    <label htmlFor="weeklySummary">Weekly Summary</label>
                    <p className="notification-description">
                      Receive weekly portfolio performance and risk summary
                    </p>
                  </div>
                  <div className="notification-control">
                    <label className="switch">
                      <input
                        type="checkbox"
                        id="weeklySummary"
                        name="notifications.weeklySummary"
                        checked={settings.notifications.weeklySummary}
                        onChange={handleChange}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'advanced' && (
            <div className="advanced-settings">
              <h3>Advanced Settings</h3>
              <p className="section-description">
                Advanced risk management parameters for experienced users
              </p>

              <div className="advanced-grid">
                <div className="advanced-card">
                  <h4>Risk Model</h4>
                  <div className="model-options">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="riskModel"
                        value="conservative"
                        defaultChecked
                      />
                      <span className="radio-label">Conservative</span>
                      <span className="radio-description">
                        Lower risk tolerance, tighter controls
                      </span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="riskModel"
                        value="balanced"
                      />
                      <span className="radio-label">Balanced</span>
                      <span className="radio-description">
                        Moderate risk tolerance, balanced approach
                      </span>
                    </label>
                    
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="riskModel"
                        value="aggressive"
                      />
                      <span className="radio-label">Aggressive</span>
                      <span className="radio-description">
                        Higher risk tolerance, wider limits
                      </span>
                    </label>
                  </div>
                </div>

                <div className="advanced-card">
                  <h4>Automated Actions</h4>
                  <div className="action-options">
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        name="autoReduce"
                        defaultChecked
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">Auto-reduce on limit breach</span>
                    </label>
                    
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        name="autoHedge"
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">Auto-hedge high correlation</span>
                    </label>
                    
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        name="dynamicStops"
                        defaultChecked
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">Dynamic stop adjustments</span>
                    </label>
                    
                    <label className="checkbox-option">
                      <input
                        type="checkbox"
                        name="positionScaling"
                      />
                      <span className="checkmark"></span>
                      <span className="option-label">Automatic position scaling</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="warning-section">
                <div className="warning-icon">⚠️</div>
                <div className="warning-content">
                  <h4>Warning: Advanced Settings</h4>
                  <p>
                    Changing these settings may significantly impact your portfolio's risk profile.
                    Ensure you understand the implications before saving changes.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="settings-actions">
          <div className="status-info">
            {lastSaved && (
              <span className="last-saved">
                Last saved: {lastSaved}
              </span>
            )}
            <span className="portfolio-id">
              Portfolio: {portfolioId}
            </span>
          </div>
          
          <div className="action-buttons">
            <button 
              className="btn btn-secondary"
              onClick={handleReset}
            >
              ↩️ Reset to Defaults
            </button>
            <button 
              className="btn btn-primary save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="spinner-small"></div>
                  Saving...
                </>
              ) : (
                '💾 Save Settings'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};